import React from 'react';
import styled, { keyframes } from 'styled-components';

export type ToastKind = 'success' | 'error' | 'info' | 'warning';

export type ToastOptions = {
  kind?: ToastKind;
  title?: string;
  durationMs?: number;
};

export function showToast(message: string, opts: ToastOptions = {}) {
  try {
    const detail = { message, ...opts };
    window.dispatchEvent(new CustomEvent('app:toast', { detail }));
  } catch { }
}

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const slideOut = keyframes`
  from { transform: translateX(0); opacity: 1; }
  to { transform: translateX(100%); opacity: 0; }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
  width: calc(100% - 40px);
  pointer-events: none;
`;

const ToastCard = styled.div<{ $kind: ToastKind }>`
  pointer-events: auto;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-left: 4px solid;
  animation: ${slideIn} 0.3s ease-out forwards;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(8px);
  background: white;

  ${props => {
    switch (props.$kind) {
      case 'success': return 'border-color: #5D722C; background: #f0f7e6; color: #4a5b23;';
      case 'error': return 'border-color: #dc2626; background: #fef2f2; color: #991b1b;';
      case 'warning': return 'border-color: #d97706; background: #fffbeb; color: #92400e;';
      case 'info': default: return 'border-color: #666666; background: #f5f5f5; color: #333333;';
    }
  }}
`;

const ToastHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ToastTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
`;

const ToastMessage = styled.div`
  font-size: 13px;
  opacity: 0.9;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
  transition: opacity 0.2s;
  color: inherit;

  &:hover {
    opacity: 1;
  }
`;

const ProgressBar = styled.div<{ $kind: ToastKind; $pct: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: ${props => props.$pct}%;
  transition: width 0.1s linear;

  ${props => {
    switch (props.$kind) {
      case 'success': return 'background: #5D722C;';
      case 'error': return 'background: #dc2626;';
      case 'warning': return 'background: #d97706;';
      case 'info': default: return 'background: #666666;';
    }
  }}
`;

const ToastIcon = styled.div`
  font-size: 18px;
`;

export const ToastHost: React.FC = () => {
  const [toasts, setToasts] = React.useState<Array<{ id: number; message: string; kind: ToastKind; title?: string; expiresAt: number; durationMs: number }>>([]);
  const idRef = React.useRef(1);

  React.useEffect(() => {
    const onToast = (e: Event) => {
      const ev = e as CustomEvent;
      const { message, kind = 'info', title, durationMs = 3500 } = (ev.detail || {}) as ToastOptions & { message: string };
      const id = idRef.current++;
      const expiresAt = Date.now() + Math.max(1500, durationMs);
      setToasts(prev => [...prev, { id, message: String(message || ''), kind, title, expiresAt, durationMs: Math.max(1500, durationMs) }]);

      window.setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, Math.max(1500, durationMs));
    };
    window.addEventListener('app:toast', onToast as any);
    return () => window.removeEventListener('app:toast', onToast as any);
  }, []);

  const getIcon = (kind: ToastKind) => {
    switch (kind) {
      case 'success': return '✅';
      case 'error': return '⛔';
      case 'warning': return '⚠️';
      case 'info': default: return 'ℹ️';
    }
  };

  return (
    <ToastContainer>
      {toasts.map(t => (
        <ToastCard key={t.id} $kind={t.kind}>
          <ToastHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ToastIcon>{getIcon(t.kind)}</ToastIcon>
              {t.title && <ToastTitle>{t.title}</ToastTitle>}
            </div>
            <CloseButton onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}>✕</CloseButton>
          </ToastHeader>
          <ToastMessage>{t.message}</ToastMessage>
          <ToastProgress expiresAt={t.expiresAt} durationMs={t.durationMs} kind={t.kind} />
        </ToastCard>
      ))}
    </ToastContainer>
  );
};

const ToastProgress: React.FC<{ expiresAt: number; durationMs: number; kind: ToastKind }> = ({ expiresAt, durationMs, kind }) => {
  const [pct, setPct] = React.useState(100);

  React.useEffect(() => {
    const interval = window.setInterval(() => {
      const remaining = Math.max(0, expiresAt - Date.now());
      setPct((remaining / durationMs) * 100);
    }, 50);
    return () => clearInterval(interval);
  }, [expiresAt, durationMs]);

  return <ProgressBar $kind={kind} $pct={pct} />;
};
